import React, { ReactNode } from "react";

import AddIcon from "@mui/icons-material/Add";

import Conditional from "../Conditional/Conditional";
import TitleBlock from "../PageHero/TitleBlock";
import Caret from "@src/assets/svg/components/Caret";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";

import styles from "./DataList.module.css";

/**
 * DataListItem component that displays a title, subtitle, and optional left and right components.
 * @param {string} title - The title text to display.
 * @param {string} subTitle - The subtitle text to display.
 * @param {ReactNode} leftComponent - The left component to display.
 * @param {ReactNode} rightComponent - The right component to display.
 * @param {string} className - Additional class names for styling.
 * @returns {JSX.Element} The rendered DataListItem component.
 */

interface Props {
  title?: string;
  subTitle?: string;
  leftComponent?: ReactNode;
  rightComponent?: ReactNode;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabled?: boolean;
  iconType?: "add" | "caret" | "none";
}

const DataListItem: React.FC<Props> = ({
  title,
  subTitle,
  leftComponent,
  rightComponent,
  className,
  onClick,
  disabled,
  iconType = "caret",
}) => {
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => (onClick !== undefined ? onClick(event) : undefined);

  const hasLeftComponent = leftComponent !== undefined;
  const hasRightComponent = rightComponent !== undefined;

  const isDisabled = disabled === true || onClick === undefined;

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      type="button"
      className={toClassName(styles.item, className)}
    >
      <Conditional condition={hasLeftComponent}>{leftComponent}</Conditional>
      <Conditional condition={!hasLeftComponent}>
        <TitleBlock
          titleVariant="title3"
          subtitleVariant="bodySmall"
          title={title ?? ""}
          subTitle={subTitle}
        />
      </Conditional>
      <div className={styles.rightContainer}>
        <Conditional condition={hasRightComponent}>
          {rightComponent}
        </Conditional>
        <Conditional condition={iconType === "caret"}>
          <Caret className={styles.caret} rotate="right" />
        </Conditional>
        <Conditional condition={iconType === "add"}>
          <AddIcon className={styles.backButtonIcon} />
        </Conditional>
      </div>
    </button>
  );
};

export default DataListItem;
